import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Card, CardBody, Col, Container, Row } from 'reactstrap';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import ReportViewer from '../ReportViewer';
import ReportHeader from '../Utils/ReportHeader';
import ReportFooter from '../Utils/ReportFooter';
import ReportFilterInfo from '../Utils/ReportFilterInfo';
import { generalStyles } from '../Utils/ReportStyling';
import Breadcrumbs from '../../CustomControls/Breadcrumb';
import { getAllRptProductData, setOnSearchChange } from '../../../store/actions/ReportListing/rptProductListing';
import Dropdown from '../../CustomControls/Dropdown';
import { CSVLink } from "react-csv";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { STATUSES } from '../../../constants/filterConstants';

import LoadingSpinner from '../../CustomControls/Spinner';

const styles = StyleSheet.create({
  productCode: {
    width: '8%'
  },
  productName: {
    width: '22%'
  },
  productTypeName: {
    width: '10%'
  },
  lastPurchaseDate: {
    width: '10%'
  },
  packageSize: {
    width: '10%'
  },
  lastPurchasePrice: {
    width: '8%'
  },
  standardCharge: {
    width: '12%'
  },
  weightOfOneUnit: {
    width: '13%'
  },
  status: {
    width: '7%'
  }
});

const ProductListingRpt = props => {
  const dispatch = useDispatch();
  const [filterStatus, setFilterStatus] = useState({ field: 'isactive', value: 'all', label: 'All' });
  const [filterProductType, setFilterProductType] = useState({ field: 'producttypeid', value: 'all', label: 'All' });
  const rptData = useSelector(state => state.rptProductListing);

  const isLoading = rptData.isLoading || false;
  const data = rptData.rptList;
  const filterData = rptData.filterData;
  const reportHeading = 'Product Listing';

  useEffect(() => {
    dispatch(getAllRptProductData(rptData.filterOptions.filterBy, rptData.filterOptions.sortBy, true));
  }, []);

  const breadCrumbPaths = [
    { id: 1, title: 'Home', path: '/' },
    { id: 2, title: `${reportHeading} Report`, path: '/reports/product-listing', isCurrent: true }
  ];

  const dropdownChange = (field, selectionOption, callSetMethod) => {
    selectionOption.field = field;
    callSetMethod(selectionOption);
  };

  const setLocalChange = currentState => {
    dispatch(setOnSearchChange(currentState));
  };

  const onGenerateHandler = () => {
    let filterSearchList = rptData.filterOptions.filterBy || [];

    filterSearchList = getFilterData(filterSearchList, filterStatus);

    filterSearchList = getFilterData(filterSearchList, filterProductType);

    setLocalChange({
      ...rptData,
      data: { ...rptData.filterOptions, filterBy: filterSearchList }
    });

    dispatch(getAllRptProductData(rptData.filterOptions.filterBy, rptData.filterOptions.sortBy));
  };

  const getFilterData = (filterSearchList, filterObj) => {
    const objIndex = filterSearchList.findIndex(x => x.name.toLowerCase() === filterObj.field);
    if (objIndex !== -1) {
      filterSearchList[objIndex].value = filterObj.value;
      filterSearchList[objIndex].valueName = filterObj.label;
    } else {
      filterSearchList.push({ name: filterObj.field, value: filterObj.value, valueName: filterObj.label });
    }
    return filterSearchList;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={`${reportHeading} Report`} paths={breadCrumbPaths} />
          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <Row className="mb-2 buttonrow">
                    <Col sm="3">
                      <div className="mb-3">
                        <Dropdown
                          placeholder="Status"
                          label="Status"
                          name="isactive"
                          options={STATUSES}
                          isMulti={false}
                          value={STATUSES.find(x => x.value === filterStatus.value)}
                          onChange={val => dropdownChange('isactive', val, setFilterStatus)}
                          validate={{
                            required: { value: false }
                          }}
                        />
                      </div>
                    </Col>
                    <Col sm="3">
                      <div className="mb-3">
                        <Dropdown
                          placeholder="All"
                          label="Product Type"
                          name="productTypeid"
                          options={filterData.productTypes}
                          isMulti={false}
                          value={filterData.productTypes.find(x => x.value === filterProductType.value)}
                          onChange={val => dropdownChange('producttypeid', val, setFilterProductType)}
                          validate={{
                            required: { value: false }
                          }}
                        />
                      </div>
                    </Col>
                    <Col sm="4">
                      <div className="text-sm-start">
                        <Button type="button" className="btn btn-primary btn-block  app-add-button" onClick={onGenerateHandler}>
                          GENERATE
                        </Button>
                      </div>
                    </Col>
                    <Col sm="2">
                      <div className="text-sm-end">
                        <CSVLink
                          data={data}
                          headers={exportHeader}
                          filename={"productlisting.csv"}
                          className="btn btn-primary download-button"
                        >
                          <FontAwesomeIcon icon={faFileExcel} /> EXPORT
                        </CSVLink>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col xl="12">
                      {isLoading ? (
                        <LoadingSpinner />
                      ) : (
                        <ReportViewer>
                          <Document title={reportHeading}>
                            <Page style={generalStyles.body} orientation="landscape">
                              <ReportHeader reportTitle={reportHeading} />
                              <ReportFilterInfo filterList={rptData.filterOptions.filterBy} />
                              <View style={generalStyles.table}>
                                <View style={generalStyles.tableRow}>
                                  <View style={[generalStyles.tableColHeader, styles.productCode]}>
                                    <Text style={generalStyles.tableCellHeader}>Code</Text>
                                  </View>
                                  <View style={[generalStyles.tableColHeader, styles.productName]}>
                                    <Text style={generalStyles.tableCellHeader}>Product Name</Text>
                                  </View>
                                  <View style={[generalStyles.tableColHeader, styles.productTypeName]}>
                                    <Text style={generalStyles.tableCellHeader}>Product Type</Text>
                                  </View>
                                  <View style={[generalStyles.tableColHeader, styles.weightOfOneUnit]}>
                                    <Text style={generalStyles.tableCellHeader}>Weight (p/unit)</Text>
                                  </View>
                                  <View style={[generalStyles.tableColHeader, styles.packageSize]}>
                                    <Text style={generalStyles.tableCellHeader}>Package Size</Text>
                                  </View>
                                  <View style={[generalStyles.tableColHeader, styles.standardCharge]}>
                                    <Text style={generalStyles.tableCellHeader}>Standard Charge</Text>
                                  </View>
                                  <View style={[generalStyles.tableColHeader, styles.lastPurchaseDate]}>
                                    <Text style={generalStyles.tableCellHeader}>Last Purchase Date</Text>
                                  </View>
                                  <View style={[generalStyles.tableColHeader, styles.lastPurchasePrice]}>
                                    <Text style={generalStyles.tableCellHeader}>Last Purchase Price</Text>
                                  </View>

                                  <View style={[generalStyles.tableColHeader, styles.status]}>
                                    <Text style={generalStyles.tableCellHeader}>Status</Text>
                                  </View>
                                </View>
                                {data.length > 0 && <ReportDataRow results={data} />}
                              </View>
                              <ReportFooter footerInfo={reportHeading}>
                                <Text
                                  style={generalStyles.pageNumber}
                                  render={({ pageNumber, totalPages }) => `Page ${pageNumber} of  ${totalPages}`}
                                />
                              </ReportFooter>
                            </Page>
                          </Document>
                        </ReportViewer>
                      )}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

const ReportDataRow = ({ results }) => {
  return (
    <View>
      {results.map(item => {
        return (
          // <reportDataRow item={n} key={n.productId}/>
          <View style={generalStyles.tableRow} key={item.productId}>
            <View style={[generalStyles.tableCol, styles.productCode]}>
              <Text style={generalStyles.tableCell}>{item.productCode}</Text>
            </View>
            <View style={[generalStyles.tableCol, styles.productName]}>
              <Text style={generalStyles.tableCell}> {item.productName} </Text>
            </View>
            <View style={[generalStyles.tableCol, styles.productTypeName]}>
              <Text style={generalStyles.tableCell}>{item.productTypeName}</Text>
            </View>
            <View style={[generalStyles.tableCol, styles.weightOfOneUnit]}>
              <Text style={generalStyles.tableCell}>{item.weightOfOneUnit}</Text>
            </View>
            <View style={[generalStyles.tableCol, styles.packageSize]}>
              <Text style={generalStyles.tableCell}>{item.packageSize}</Text>
            </View>
            <View style={[generalStyles.tableCol, styles.standardCharge]}>
              <Text style={generalStyles.tableCell}>{item.standardCharge}</Text>
            </View>
            <View style={[generalStyles.tableCol, styles.lastPurchaseDate]}>
              <Text style={generalStyles.tableCell}>{item.lastPurchaseDate}</Text>
            </View>
            <View style={[generalStyles.tableCol, styles.lastPurchasePrice]}>
              <Text style={generalStyles.tableCell}>{item.lastPurchasePrice}</Text>
            </View>

            <View style={[generalStyles.tableCol, styles.status]}>
              <Text style={generalStyles.tableCell}>{item.isActive}</Text>
            </View>
          </View>
        );
      })}
    </View>
  );
};

export default ProductListingRpt;


const exportHeader = [
  { label: "Code", key: "productCode" },
  { label: "Product Name", key: "clientName" },
  { label: "Product Type", key: "productName" },
  { label: "Weight (p/unit)", key: "weightOfOneUnit" },
  { label: "Package Size", key: "packageSize" },
  { label: "Standard Charge", key: "standardCharge" },
  { label: "Last PurchaseDate", key: "lastPurchaseDate" },
  { label: "Last Purchase Price", key: "lastPurchasePrice" },
  { label: "Status", key: "isActive" },
];